import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import { allProducts, productCategories } from "@/mocks/products";

export default function ProductsPage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("전체");

  const filtered = allProducts.filter((p) => {
    const matchCat = activeCategory === "전체" || p.category === activeCategory;
    const matchSearch =
      !search ||
      p.name.includes(search) ||
      p.application.includes(search) ||
      p.material.includes(search) ||
      p.subcategory.includes(search);
    return matchCat && matchSearch;
  });

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title="제품소개"
        subtitle="50년 기술력으로 완성하는 고품질 고무제품 — 7개 카테고리, 맞춤형 솔루션"
        breadcrumb={[{ label: "제품소개" }]}
        bgImage="https://readdy.ai/api/search-image?query=Various%20black%20industrial%20rubber%20parts%20and%20components%20arranged%20neatly%20on%20clean%20workshop%20surface%2C%20professional%20product%20photography%2C%20O-rings%20gaskets%20seals%20bushings%20of%20different%20sizes%2C%20engineering%20precision%2C%20soft%20studio%20lighting%2C%20organized%20layout&width=1600&height=680&seq=krubber-products-banner&orientation=landscape"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          {/* Category Filter */}
          <div className="flex items-center gap-2 flex-wrap mb-8 justify-center">
            {productCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Search + Count */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
            <p className="text-sm text-foreground-600">
              총 <strong className="text-foreground-950">{filtered.length}</strong>개 제품
              {activeCategory !== "전체" && (
                <span className="text-foreground-500"> / {activeCategory}</span>
              )}
            </p>
            <div className="relative w-full sm:w-[300px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" />
              </span>
              <input
                type="text"
                placeholder="제품명, 적용분야, 재질 검색"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <span className="w-16 h-16 mx-auto rounded-2xl bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-foreground-400 text-2xl" />
              </span>
              <p className="mt-4 text-foreground-600 text-sm">검색 결과가 없습니다.</p>
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setActiveCategory("전체");
                }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              >
                <i className="ri-refresh-line" />
                필터 초기화
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
              {filtered.map((product) => (
                <Link
                  key={product.id}
                  to={`/products/${product.id}`}
                  className="group rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-200/60 overflow-hidden transition-all cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden bg-background-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="inline-block px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                        {product.category}
                      </span>
                      <span className="inline-block px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                        {product.subcategory}
                      </span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>
                    <div className="mt-3 flex flex-col gap-1.5">
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-settings-3-line text-[11px]" />
                        {product.application}
                      </span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-drop-line text-[11px]" />
                        {product.material}
                      </span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                        <i className="ri-contrast-2-line text-[11px]" />
                        {product.hardness}
                      </span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-background-200/70 flex items-center justify-between">
                      <span className="text-xs font-semibold text-primary-600 group-hover:translate-x-1 transition-transform inline-flex items-center gap-1">
                        상세 보기
                        <i className="ri-arrow-right-line text-[10px]" />
                      </span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-temp-hot-line text-[11px]" />
                        {product.tempRange}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}